import { useMemo } from 'react';
import {
  Callout,
  Classes,
  Icon,
  InputGroup,
  Section,
  SectionCard,
  Tooltip,
} from '@blueprintjs/core';
import { debounceFunction } from '@axo/shared/util/fp';
import { acme_hooks } from '@axo/shared/data-access/hooks';
import { acme } from '@axo/shared/data-access/types';
import { AceTable } from '../../../components/AceTable';
import { ITableConfig } from '../../../models/tableConfig';
import { ApplicationLink } from '../../../components/ApplicationLink';
import { formatDatetime } from '../../../utils/formatDate';
import { StatusTag } from '../../../components/StatusTag';
import { useArchiveSearchActions, useArchiveSearch } from '../../../store';
import { AceTag } from '../../../components/AceTag';

interface Props {
  marketCountry: string;
}

export function ArchiveSearchTab({ marketCountry }: Props) {
  const search = useArchiveSearch();
  const { replaceSearch } = useArchiveSearchActions();

  const setQueryDebounced = useMemo(
    () =>
      debounceFunction(
        (query: string) =>
          replaceSearch((prev) => ({
            ...prev,
            query,
          })),
        800,
      ),
    [replaceSearch],
  );

  const results = acme_hooks.useArchiveSearch(search);

  const items: RowType[] = useMemo(
    () =>
      results.data?.Items?.map((item) => ({
        ...item,
        UniqueKey: `${item.LoanApplicationID}-${item.Role}`,
      })).sort((a, b) => (a.CreatedAt < b.CreatedAt ? 1 : -1)) ?? [],
    [results.data?.Items],
  );

  const tableConfig = useMemo(() => config(marketCountry), [marketCountry]);

  return (
    <Section title="Archive Search" icon="data-search">
      <SectionCard>
        <InputGroup
          type="search"
          placeholder="Search by email, phone or SSN"
          autoComplete="off"
          asyncControl={true}
          leftIcon="search"
          name="query"
          value={search.query}
          onChange={(e) => setQueryDebounced(e.currentTarget.value)}
        />
      </SectionCard>

      {results.data?.Sketchy && (
        <SectionCard style={{ textAlign: 'left' }}>
          <Callout intent="danger" icon="info-sign">
            <b>Notice:</b> Some results may be missing.
          </Callout>
        </SectionCard>
      )}

      <SectionCard>
        <Callout minimal compact>
          <AceTag>
            {(results.isLoading || results.isIdle ? '-' : `${items.length}`) +
              ' applications'}
          </AceTag>
        </Callout>

        <AceTable<RowType>
          config={tableConfig}
          data={items}
          error={
            results.error instanceof Error ? results.error.message : undefined
          }
          uniqueRowIDKey="UniqueKey"
          loading={results.isLoading}
        />
      </SectionCard>
    </Section>
  );
}

type RowType = acme.ArchiveResult & { UniqueKey: string };

const config = (currentMarket: string): ITableConfig<RowType> => ({
  columns: {
    HumanReadableID: {
      label: 'Application Number',
      render: (_, res) =>
        res.MarketCountry !== currentMarket ? (
          <Tooltip
            content={
              <span>
                This application is in a different market.
                <br />
                Switch market if you need to see the application.
              </span>
            }
          >
            <em
              className={Classes.TEXT_MUTED}
              style={{ cursor: 'not-allowed' }}
            >
              {res.HumanReadableID}
            </em>
          </Tooltip>
        ) : (
          <ApplicationLink
            id={res.LoanApplicationID}
            humanReadableID={res.HumanReadableID}
            marketCountry={res.MarketCountry}
          />
        ),
    },

    MarketCountry: {
      label: 'Market',
      render: (_, res) => res.MarketCountry,
    },

    Role: {
      label: 'Applicant Role',
      render: (_, res) => roleFormat[res.Role],
    },

    Product: {
      label: 'Product',
      render: (_, res) => res.Product,
    },

    Broker: {
      label: 'Broker',
      render: (_, res) => res.Broker,
    },

    CreatedAt: {
      label: 'Application Created',
      labelIcon: <Icon icon="chevron-down" />,
      render: (_, res) => formatDatetime(res.CreatedAt),
    },

    CompletedAt: {
      label: 'Application Completed',
      render: (_, res) => formatDatetime(res.CompletedAt),
    },

    Status: {
      label: 'Status',
      render: (_, res) => (
        <StatusTag status={res.Status}>{res.Status}</StatusTag>
      ),
    },

    UpdatedAt: {
      label: 'Last updated',
      render: (_, res) => formatDatetime(res.UpdatedAt),
    },
  },
});

const roleFormat: Record<acme.Role, string> = {
  '': '',
  customer: 'Main',
  'co-customer': 'Co',
  person: 'Main',
  'co-person': 'Co',
};
